import React, { useEffect, useRef, useState, useMemo } from 'react';
import { debounce } from 'lodash-es';
import {
  GUIDE_TIP_TITLE,
  GUIDE_TIP_DESC,
  GUIDE_TIP_ITEMS,
  type GuideTipItem,
} from './guideTipConfig';
import './GuideTip.less';
import { ArrowContinueLineIcon } from '@kim/ui';

const CHIP_CLICK_DEBOUNCE_MS = 300;

const DEFAULT_MARGIN_TOP = 180;
const BOTTOM_THRESHOLD = 160;

interface GuideTipProps {
  agentType?: string;
  onSelectItem?: (item: GuideTipItem) => void;
}

const GuideTip: React.FC<GuideTipProps> = ({ agentType, onSelectItem }) => {
  const elRef = useRef<HTMLDivElement>(null);
  const [marginTop, setMarginTop] = useState(DEFAULT_MARGIN_TOP);

  useEffect(() => {
    const el = elRef.current;
    if (!el) return;

    const update = () => {
      const parent = el.parentElement;
      if (!parent) return;
      // 以 DEFAULT_MARGIN_TOP 为基准，计算剩余底部空间
      const spaceBelow =
        parent.clientHeight - DEFAULT_MARGIN_TOP - el.offsetHeight;
      if (spaceBelow >= BOTTOM_THRESHOLD) {
        setMarginTop(DEFAULT_MARGIN_TOP);
      } else {
        setMarginTop(DEFAULT_MARGIN_TOP - (BOTTOM_THRESHOLD - spaceBelow));
      }
    };

    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (el.parentElement) ro.observe(el.parentElement);
    update();

    return () => ro.disconnect();
  }, []);

  const debouncedSelect = useMemo(
    () =>
      debounce(
        (item: GuideTipItem) => onSelectItem?.(item),
        CHIP_CLICK_DEBOUNCE_MS,
      ),
    [onSelectItem],
  );

  const handleChipClick = (item: GuideTipItem) => {
    if (item.id === 'joingroup') {
      debouncedSelect(item);
    } else {
      onSelectItem?.(item);
    }
  };

  return (
    <div className="claw-guide-tip" ref={elRef} style={{ marginTop }}>
      <h1
        className="claw-guide-tip-title"
        // onClick={onSecretClick}
        style={{ cursor: 'default', userSelect: 'none' }}
      >
        {GUIDE_TIP_TITLE}
      </h1>
      <p className="claw-guide-tip-desc">
        {GUIDE_TIP_DESC.split('\n').map((line, i, arr) => (
          <React.Fragment key={i}>
            {line}
            {i < arr.length - 1 && <br />}
          </React.Fragment>
        ))}
      </p>
      <div className="claw-guide-tip-chips">
        {GUIDE_TIP_ITEMS.filter((item) =>
          agentType === 'mario-agent'
            ? !['joingroup', 'fortune'].includes(item.id)
            : true,
        ).map((item) => (
          <button
            key={item.id}
            className={`claw-guide-tip-chip claw-guide-tip-chip-${item.id}`}
            onClick={() => handleChipClick(item)}
          >
            <div className="claw-guide-tip-chip-icon">
              <img src={item.icon} alt={item.label} />
            </div>
            <span className="claw-guide-tip-chip-label">{item.label}</span>
            <ArrowContinueLineIcon
              className="claw-guide-tip-chip-arrow"
              aria-hidden
              size={16}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

export default GuideTip;
